import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';
import { promoOffers, promoCodes, promoProducts, promoCategories } from '@/mocks/promos';

/* ─── Types ─── */
type DiscountRange = 'all' | 'up15' | '15to25' | '25to40' | '40plus';
type DeadlineFilter = 'all' | 'today' | '3days' | 'week' | 'month';
type TabType = 'offers' | 'codes' | 'products';

const DISCOUNT_OPTIONS: { id: DiscountRange; label: string }[] = [
  { id: 'all', label: 'Любая скидка' },
  { id: 'up15', label: 'До 15%' },
  { id: '15to25', label: '15–25%' },
  { id: '25to40', label: '25–40%' },
  { id: '40plus', label: 'Более 40%' },
];

const DEADLINE_OPTIONS: { id: DeadlineFilter; label: string; icon: string }[] = [
  { id: 'all', label: 'Любой срок', icon: 'ri-calendar-line' },
  { id: 'today', label: 'Сегодня', icon: 'ri-time-line' },
  { id: '3days', label: 'До 3 дней', icon: 'ri-calendar-2-line' },
  { id: 'week', label: 'До недели', icon: 'ri-calendar-check-line' },
  { id: 'month', label: 'До месяца', icon: 'ri-calendar-event-line' },
];

const extractPercent = (discount: string): number => {
  const m = discount.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
};

const daysLeft = (endsAt: string): number => {
  const diff = new Date(endsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
};

const matchesDiscount = (pct: number, range: DiscountRange): boolean => {
  if (range === 'all') return true;
  if (range === 'up15') return pct < 15;
  if (range === '15to25') return pct >= 15 && pct <= 25;
  if (range === '25to40') return pct > 25 && pct <= 40;
  if (range === '40plus') return pct > 40;
  return true;
};

const matchesDeadline = (endsAt: string, filter: DeadlineFilter): boolean => {
  const d = daysLeft(endsAt);
  if (filter === 'all') return true;
  if (filter === 'today') return d <= 1;
  if (filter === '3days') return d <= 3;
  if (filter === 'week') return d <= 7;
  if (filter === 'month') return d <= 30;
  return true;
};

/* ─── Offer Card ─── */
const OfferCard = ({ offer }: { offer: typeof promoOffers[0] }) => {
  const [copied, setCopied] = useState(false);
  const days = daysLeft(offer.endsAt);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    if (offer.code) {
      navigator.clipboard.writeText(offer.code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Link
      to={`/promos/${offer.id}`}
      className="group relative rounded-2xl overflow-hidden flex flex-col transition-all duration-300 cursor-pointer"
      style={{ border: `1px solid ${offer.color}20`, background: 'rgba(255,255,255,0.02)' }}
    >
      {/* Top accent */}
      <div className="h-0.5" style={{ background: `linear-gradient(90deg, ${offer.color}, ${offer.colorSecondary}, transparent)` }} />

      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: 160 }}>
        <img src={offer.image} alt={offer.title} className="w-full h-full object-cover object-top opacity-60 transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div
          className="absolute top-3 left-3 px-2.5 py-1 rounded-full font-mono text-[9px] font-bold"
          style={{ background: offer.badgeColor, color: '#050508' }}
        >
          {offer.badge}
        </div>
        <div
          className="absolute top-3 right-3 w-12 h-12 flex items-center justify-center rounded-xl font-orbitron font-black text-base"
          style={{ background: `${offer.color}20`, border: `2px solid ${offer.color}`, color: offer.color }}
        >
          {offer.discount}
        </div>
        {days <= 3 && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[9px]"
            style={{ background: 'rgba(255,45,120,0.2)', border: '1px solid rgba(255,45,120,0.4)', color: '#ff2d78' }}>
            <i className="ri-alarm-line" />
            Осталось {days === 0 ? 'менее дня' : `${days} дн.`}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-orbitron font-bold text-sm text-white leading-snug mb-1 group-hover:opacity-75 transition-opacity">{offer.title}</h3>
        <p className="font-mono text-xs mb-3 line-clamp-2" style={{ color: offer.color }}>{offer.subtitle}</p>
        <p className="font-mono text-xs text-white/35 leading-relaxed line-clamp-2 mb-4">{offer.description}</p>

        <div className="mt-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 font-mono text-[10px] text-white/35">
            <i className="ri-calendar-line" />
            До {new Date(offer.endsAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
          </div>
          {offer.code && (
            <button
              onClick={handleCopy}
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-[10px] cursor-pointer transition-all"
              style={copied
                ? { background: '#39ff14', color: '#050508' }
                : { border: `1px dashed ${offer.color}40`, color: offer.color, background: `${offer.color}08` }}
            >
              <i className={copied ? 'ri-check-line' : 'ri-coupon-line'} />
              {copied ? 'Скопировано!' : offer.code}
            </button>
          )}
        </div>
      </div>
    </Link>
  );
};

/* ─── Code Card ─── */
const CodeCard = ({ code }: { code: typeof promoCodes[0] }) => {
  const [copied, setCopied] = useState(false);
  const progress = Math.round(((code.usesTotal - code.usesLeft) / code.usesTotal) * 100);

  const handleCopy = () => {
    navigator.clipboard.writeText(code.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-4 transition-all duration-300"
      style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${code.color}20` }}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
          style={{ background: `${code.color}15`, border: `1px solid ${code.color}30` }}>
          <i className={`${code.icon} text-base`} style={{ color: code.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-orbitron font-bold text-sm text-white">{code.title}</h3>
            {code.isHot && (
              <span className="px-2 py-0.5 rounded-full font-mono text-[9px] font-bold" style={{ background: '#ff2d78', color: '#050508' }}>HOT</span>
            )}
            {code.isNew && (
              <span className="px-2 py-0.5 rounded-full font-mono text-[9px] font-bold" style={{ background: '#39ff14', color: '#050508' }}>NEW</span>
            )}
          </div>
          <p className="font-mono text-xs text-white/40 line-clamp-2">{code.description}</p>
        </div>
        <div className="font-orbitron font-black text-lg shrink-0" style={{ color: code.color }}>{code.discount}</div>
      </div>

      {/* Progress */}
      <div>
        <div className="flex justify-between font-mono text-[10px] text-white/30 mb-1.5">
          <span>Использовано: {code.usesTotal - code.usesLeft} из {code.usesTotal}</span>
          <span style={{ color: code.color }}>{code.usesLeft} осталось</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: code.color }} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={handleCopy}
          className="whitespace-nowrap flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all"
          style={copied
            ? { background: '#39ff14', color: '#050508' }
            : { background: `${code.color}15`, border: `1px dashed ${code.color}40`, color: code.color }}
        >
          <i className={copied ? 'ri-check-line' : 'ri-coupon-3-line'} />
          {copied ? 'Скопировано!' : code.code}
        </button>
        <div className="font-mono text-[10px] text-white/30 text-right shrink-0">
          <div>от {code.minOrder.toLocaleString()} ₽</div>
          <div>до {new Date(code.expiresAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}</div>
        </div>
      </div>
    </div>
  );
};

/* ─── Product Card ─── */
const ProductCard = ({ product }: { product: typeof promoProducts[0] }) => (
  <Link
    to={`/product/${product.id}`}
    className="group rounded-2xl overflow-hidden flex flex-col transition-all duration-300 cursor-pointer"
    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
  >
    <div className="relative overflow-hidden" style={{ height: 180 }}>
      <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
      <div
        className="absolute top-3 left-3 px-2.5 py-1 rounded-full font-orbitron font-black text-xs"
        style={{ background: product.color, color: '#050508' }}
      >
        -{product.discount}%
      </div>
    </div>
    <div className="p-4 flex flex-col flex-1">
      <span className="font-mono text-[10px] mb-2 px-2 py-0.5 rounded-full self-start"
        style={{ background: `${product.color}12`, color: product.color, border: `1px solid ${product.color}25` }}>
        {product.promoTag}
      </span>
      <h3 className="font-orbitron font-bold text-sm text-white leading-snug mb-1 line-clamp-2 group-hover:opacity-75 transition-opacity">{product.name}</h3>
      <p className="font-mono text-xs text-white/35 mb-3 line-clamp-1">{product.description}</p>
      <div className="flex items-end gap-2 mt-auto">
        <span className="font-orbitron font-black text-lg" style={{ color: product.color }}>{product.price.toLocaleString()} ₽</span>
        <span className="font-mono text-xs text-white/30 line-through mb-0.5">{product.oldPrice.toLocaleString()} ₽</span>
      </div>
    </div>
  </Link>
);

/* ─── Main Page ─── */
const PromoSearchPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('offers');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [discountRange, setDiscountRange] = useState<DiscountRange>('all');
  const [deadline, setDeadline] = useState<DeadlineFilter>('all');
  const [hasCode, setHasCode] = useState(false);

  const filteredOffers = useMemo(() => {
    return promoOffers.filter((o) => {
      const q = query.toLowerCase();
      const matchQ = !q || o.title.toLowerCase().includes(q) || o.description.toLowerCase().includes(q) || o.subtitle.toLowerCase().includes(q);
      const matchCat = selectedCategory === 'all' || o.tag === selectedCategory;
      const matchDisc = matchesDiscount(extractPercent(o.discount), discountRange);
      const matchDead = matchesDeadline(o.endsAt, deadline);
      const matchCode = !hasCode || !!o.code;
      return matchQ && matchCat && matchDisc && matchDead && matchCode;
    });
  }, [query, selectedCategory, discountRange, deadline, hasCode]);

  const filteredCodes = useMemo(() => {
    return promoCodes.filter((c) => {
      const q = query.toLowerCase();
      const matchQ = !q || c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
      const matchDisc = matchesDiscount(extractPercent(c.discount), discountRange);
      const matchDead = matchesDeadline(c.expiresAt, deadline);
      return matchQ && matchDisc && matchDead;
    });
  }, [query, discountRange, deadline]);

  const filteredProducts = useMemo(() => {
    return promoProducts.filter((p) => {
      const q = query.toLowerCase();
      const matchQ = !q || p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
      const matchCat = selectedCategory === 'all' || p.category === selectedCategory;
      const matchDisc = matchesDiscount(p.discount, discountRange);
      return matchQ && matchCat && matchDisc;
    });
  }, [query, selectedCategory, discountRange]);

  const activeCount = activeTab === 'offers' ? filteredOffers.length : activeTab === 'codes' ? filteredCodes.length : filteredProducts.length;

  const hasActiveFilters = query || selectedCategory !== 'all' || discountRange !== 'all' || deadline !== 'all' || hasCode;

  const resetFilters = () => {
    setQuery('');
    setSelectedCategory('all');
    setDiscountRange('all');
    setDeadline('all');
    setHasCode(false);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    name: 'Поиск акций NeonPharma',
    url: `${SITE_URL}/promos/search`,
  };

  return (
    <div className="min-h-screen" style={{ background: '#050508' }}>
      <SeoHead
        title="Поиск акций и скидок — NeonPharma | Фильтр по категории и скидке"
        description="Найдите лучшие акции интернет-аптеки NeonPharma. Фильтрация по категории, размеру скидки и сроку действия. Промокоды, акционные товары."
        keywords="поиск акций аптека, скидки лекарства фильтр, промокоды NeonPharma"
        canonical="/promos/search"
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-10 px-6 lg:px-12 overflow-hidden">
        <div className="absolute top-10 left-1/4 w-80 h-80 rounded-full opacity-8" style={{ background: '#ff2d78', filter: 'blur(120px)' }} />
        <div className="absolute top-0 right-1/3 w-64 h-64 rounded-full opacity-6" style={{ background: '#00f5ff', filter: 'blur(100px)' }} />

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 font-mono text-xs text-white/30 mb-8 flex-wrap">
            <Link to="/" className="hover:text-white transition-colors">Главная</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/promos" className="hover:text-white transition-colors">Акции</Link>
            <i className="ri-arrow-right-s-line" />
            <span style={{ color: '#ff2d78' }}>Поиск</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl"
                  style={{ background: 'rgba(255,45,120,0.15)', border: '1px solid rgba(255,45,120,0.4)' }}>
                  <i className="ri-search-line text-lg" style={{ color: '#ff2d78' }} />
                </div>
                <p className="font-mono text-xs tracking-[0.3em]" style={{ color: 'rgba(0,245,255,0.7)' }}>ПОИСК ПО АКЦИЯМ</p>
              </div>
              <h1 className="font-orbitron font-black text-4xl md:text-5xl text-white leading-tight">
                НАЙТИ<br />
                <span style={{ color: '#ff2d78', textShadow: '0 0 30px #ff2d7880' }}>АКЦИЮ</span>
              </h1>
              <p className="font-mono text-sm mt-3 text-white/40 max-w-md">
                Фильтруйте акции по категории, размеру скидки и сроку действия
              </p>
            </div>

            {/* Stats */}
            <div className="flex gap-3 flex-wrap">
              {[
                { val: promoOffers.length, label: 'Акций', color: '#ff2d78' },
                { val: promoCodes.length, label: 'Промокодов', color: '#00f5ff' },
                { val: promoProducts.length, label: 'Товаров', color: '#39ff14' },
              ].map((s) => (
                <div key={s.label} className="px-4 py-3 rounded-xl text-center"
                  style={{ background: `${s.color}10`, border: `1px solid ${s.color}25` }}>
                  <div className="font-orbitron font-black text-2xl" style={{ color: s.color }}>{s.val}</div>
                  <div className="font-mono text-[10px] mt-0.5 text-white/35">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Search bar */}
          <div className="relative mb-6">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-base text-white/30" />
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по названию акции, промокоду или товару..."
              className="w-full pl-11 pr-12 py-4 rounded-2xl font-mono text-sm outline-none transition-all"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: query ? '1px solid rgba(255,45,120,0.5)' : '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.85)',
              }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full cursor-pointer transition-all"
                style={{ background: 'rgba(255,255,255,0.08)' }}
              >
                <i className="ri-close-line text-sm text-white/50" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Filters + Results */}
      <section className="px-6 lg:px-12 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">

            {/* ── Sidebar filters ── */}
            <aside className="w-full lg:w-72 shrink-0">
              <div className="sticky top-24 space-y-5">

                {/* Reset */}
                {hasActiveFilters && (
                  <button
                    onClick={resetFilters}
                    className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all"
                    style={{ border: '1px solid rgba(255,45,120,0.4)', color: '#ff2d78', background: 'rgba(255,45,120,0.06)' }}
                  >
                    <i className="ri-refresh-line" />
                    Сбросить все фильтры
                  </button>
                )}

                {/* Category */}
                <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <h3 className="font-orbitron font-bold text-sm text-white mb-4 flex items-center gap-2">
                    <i className="ri-price-tag-3-line" style={{ color: '#ff2d78' }} />
                    Категория
                  </h3>
                  <div className="space-y-1.5">
                    {promoCategories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className="whitespace-nowrap w-full flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all text-left"
                        style={selectedCategory === cat.id
                          ? { background: `${cat.color}15`, border: `1px solid ${cat.color}35` }
                          : { background: 'transparent', border: '1px solid transparent' }}
                      >
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                          style={{ background: `${cat.color}12`, border: `1px solid ${cat.color}20` }}>
                          <i className={`${cat.icon} text-xs`} style={{ color: cat.color }} />
                        </div>
                        <span className="font-mono text-xs" style={{ color: selectedCategory === cat.id ? cat.color : 'rgba(255,255,255,0.55)' }}>
                          {cat.label}
                        </span>
                        {selectedCategory === cat.id && (
                          <i className="ri-check-line ml-auto text-xs" style={{ color: cat.color }} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Discount range */}
                <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <h3 className="font-orbitron font-bold text-sm text-white mb-4 flex items-center gap-2">
                    <i className="ri-percent-line" style={{ color: '#00f5ff' }} />
                    Размер скидки
                  </h3>
                  <div className="space-y-1.5">
                    {DISCOUNT_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setDiscountRange(opt.id)}
                        className="whitespace-nowrap w-full flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer transition-all"
                        style={discountRange === opt.id
                          ? { background: 'rgba(0,245,255,0.1)', border: '1px solid rgba(0,245,255,0.3)' }
                          : { background: 'transparent', border: '1px solid transparent' }}
                      >
                        <span className="font-mono text-xs" style={{ color: discountRange === opt.id ? '#00f5ff' : 'rgba(255,255,255,0.55)' }}>
                          {opt.label}
                        </span>
                        {discountRange === opt.id && <i className="ri-check-line text-xs" style={{ color: '#00f5ff' }} />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Deadline */}
                <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <h3 className="font-orbitron font-bold text-sm text-white mb-4 flex items-center gap-2">
                    <i className="ri-calendar-line" style={{ color: '#39ff14' }} />
                    Срок действия
                  </h3>
                  <div className="space-y-1.5">
                    {DEADLINE_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setDeadline(opt.id)}
                        className="whitespace-nowrap w-full flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all"
                        style={deadline === opt.id
                          ? { background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.3)' }
                          : { background: 'transparent', border: '1px solid transparent' }}
                      >
                        <i className={`${opt.icon} text-xs`} style={{ color: deadline === opt.id ? '#39ff14' : 'rgba(255,255,255,0.3)' }} />
                        <span className="font-mono text-xs" style={{ color: deadline === opt.id ? '#39ff14' : 'rgba(255,255,255,0.55)' }}>
                          {opt.label}
                        </span>
                        {deadline === opt.id && <i className="ri-check-line ml-auto text-xs" style={{ color: '#39ff14' }} />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Has promo code toggle */}
                <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <button
                    onClick={() => setHasCode((v) => !v)}
                    className="whitespace-nowrap w-full flex items-center justify-between cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg"
                        style={{ background: 'rgba(240,255,0,0.12)', border: '1px solid rgba(240,255,0,0.2)' }}>
                        <i className="ri-coupon-3-line text-xs" style={{ color: '#f0ff00' }} />
                      </div>
                      <span className="font-mono text-xs text-white/60">Только с промокодом</span>
                    </div>
                    <div
                      className="w-10 h-5 rounded-full transition-all duration-300 relative"
                      style={{ background: hasCode ? '#f0ff00' : 'rgba(255,255,255,0.1)' }}
                    >
                      <div
                        className="absolute top-0.5 w-4 h-4 rounded-full transition-all duration-300"
                        style={{ background: hasCode ? '#050508' : 'rgba(255,255,255,0.4)', left: hasCode ? '22px' : '2px' }}
                      />
                    </div>
                  </button>
                </div>
              </div>
            </aside>

            {/* ── Results ── */}
            <div className="flex-1 min-w-0">
              {/* Tabs */}
              <div className="flex items-center gap-1 p-1 rounded-xl mb-6 w-fit"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                {([
                  { id: 'offers' as TabType, label: 'Акции', count: filteredOffers.length, color: '#ff2d78' },
                  { id: 'codes' as TabType, label: 'Промокоды', count: filteredCodes.length, color: '#00f5ff' },
                  { id: 'products' as TabType, label: 'Товары', count: filteredProducts.length, color: '#39ff14' },
                ] as const).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="whitespace-nowrap flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-xs cursor-pointer transition-all duration-200"
                    style={activeTab === tab.id
                      ? { background: `${tab.color}18`, color: tab.color, border: `1px solid ${tab.color}35` }
                      : { color: 'rgba(255,255,255,0.4)', border: '1px solid transparent' }}
                  >
                    {tab.label}
                    <span
                      className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                      style={{ background: activeTab === tab.id ? `${tab.color}25` : 'rgba(255,255,255,0.08)', color: activeTab === tab.id ? tab.color : 'rgba(255,255,255,0.3)' }}
                    >
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Active filters chips */}
              {hasActiveFilters && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {query && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs"
                      style={{ background: 'rgba(255,45,120,0.12)', border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }}>
                      <i className="ri-search-line text-[10px]" />
                      «{query}»
                      <button onClick={() => setQuery('')} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                    </span>
                  )}
                  {selectedCategory !== 'all' && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs"
                      style={{ background: 'rgba(255,107,0,0.12)', border: '1px solid rgba(255,107,0,0.3)', color: '#ff6b00' }}>
                      {promoCategories.find((c) => c.id === selectedCategory)?.label}
                      <button onClick={() => setSelectedCategory('all')} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                    </span>
                  )}
                  {discountRange !== 'all' && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs"
                      style={{ background: 'rgba(0,245,255,0.12)', border: '1px solid rgba(0,245,255,0.3)', color: '#00f5ff' }}>
                      {DISCOUNT_OPTIONS.find((d) => d.id === discountRange)?.label}
                      <button onClick={() => setDiscountRange('all')} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                    </span>
                  )}
                  {deadline !== 'all' && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs"
                      style={{ background: 'rgba(57,255,20,0.12)', border: '1px solid rgba(57,255,20,0.3)', color: '#39ff14' }}>
                      {DEADLINE_OPTIONS.find((d) => d.id === deadline)?.label}
                      <button onClick={() => setDeadline('all')} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                    </span>
                  )}
                  {hasCode && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs"
                      style={{ background: 'rgba(240,255,0,0.12)', border: '1px solid rgba(240,255,0,0.3)', color: '#f0ff00' }}>
                      С промокодом
                      <button onClick={() => setHasCode(false)} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                    </span>
                  )}
                </div>
              )}

              {/* Result count */}
              <p className="font-mono text-xs text-white/30 mb-6">
                Найдено: <span className="text-white/60">{activeCount}</span> результатов
              </p>

              {/* Grid */}
              {activeTab === 'offers' && (
                filteredOffers.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {filteredOffers.map((o) => <OfferCard key={o.id} offer={o} />)}
                  </div>
                ) : <EmptyState />
              )}

              {activeTab === 'codes' && (
                filteredCodes.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {filteredCodes.map((c) => <CodeCard key={c.id} code={c} />)}
                  </div>
                ) : <EmptyState />
              )}

              {activeTab === 'products' && (
                filteredProducts.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {filteredProducts.map((p) => <ProductCard key={p.id} product={p} />)}
                  </div>
                ) : <EmptyState />
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-16 h-16 flex items-center justify-center rounded-2xl mb-5"
      style={{ background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.2)' }}>
      <i className="ri-search-line text-2xl" style={{ color: 'rgba(255,45,120,0.5)' }} />
    </div>
    <h3 className="font-orbitron font-bold text-lg text-white mb-2">Ничего не найдено</h3>
    <p className="font-mono text-sm text-white/35 max-w-xs">
      Попробуйте изменить параметры поиска или сбросить фильтры
    </p>
  </div>
);

export default PromoSearchPage;
